/**
 * DeckBimViewer – deck.gl-based 2D BIM viewer.
 *
 * Props interface is intentionally identical so callers only need to swap the import.
 * All geometry rendering runs on the GPU via WebGL/WebGPU (deck.gl).
 *
 * Coordinate system:
 *   Input points are in model space [x, y].
 *   We apply the same flip ([x,y]â†’[y,x]) and clockwise rotation that PixBimViewer
 *   used internally, so the visual output is equivalent.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DeckGL from "@deck.gl/react";
import type { DeckGLRef } from "@deck.gl/react";
import { OrthographicView } from "@deck.gl/core";
import type { PickingInfo } from "@deck.gl/core";
import {
  SolidPolygonLayer,
  PolygonLayer,
  PathLayer,
  LineLayer,
  ScatterplotLayer,
  TextLayer,
} from "@deck.gl/layers";

import type {
  IPixBimCameraState,
  IPixCircle,
  IPixDimensionLine,
  IPixPath,
  IPixPolygon,
  PixBimViewerAction,
} from "./types";
import { getBoundingBox } from "./geo";
import { hexToRgba, parseStrokeWidth, parseFontSize } from "./colorUtils";
import {
  computeTransformedData,
  computeFitZoom,
  bboxCenter,
  polygonCentroid,
  invertTransform,
} from "./transformUtils";
import type { BBox, Pos2, TransformedPolygon } from "./transformUtils";

// ---------------------------------------------------------------------------
// Types (identical to PixBimViewer public API)
// ---------------------------------------------------------------------------

export type {
  IPixBimCameraState,
  IPixCircle,
  IPixDimensionLine,
  IPixPath,
  IPixPolygon,
  PixBimViewerAction,
};

interface DeckBimViewerProps {
  polygons: IPixPolygon[];
  paths?: IPixPath[];
  circles?: IPixCircle[];
  dimensionLines?: IPixDimensionLine[];
  angle?: number;
  onMoveStart?: (id: string) => void;
  onClick?: (id: string | undefined, coordinates: [number, number]) => void;
  onDragAndDrop?: (id: string, points: number[][]) => void;
  onRightMenuClick?: (action: string, polygon: IPixPolygon) => void;
  onDragSelected?: (ids: string[]) => void;
  pointerTracker?: { color: string; size: number };
  goToPolygon?: string;
  action?: PixBimViewerAction;
  zoomExtentKey?: string | number | null;
  useLeftClickToPan?: boolean;
  flip2dCoordinate?: boolean;
  zoom?: number;
  centerWorld?: { x: number; y: number };
  onCameraChange?: (camera: IPixBimCameraState) => void;
}

// ---------------------------------------------------------------------------
// Internal types
// ---------------------------------------------------------------------------

interface ViewState {
  target: [number, number, number];
  zoom: number;
  minZoom: number;
  maxZoom: number;
}

interface DragState {
  polygonId: string;
  originalPoints: Pos2[];
  startWorld: Pos2;
}

interface SelectState {
  startScreen: Pos2;
  currentScreen: Pos2;
}

interface TooltipState {
  texts: string[];
  x: number;
  y: number;
}

interface ContextMenuState {
  polygon: IPixPolygon;
  x: number;
  y: number;
}

type RectBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

type SelectablePolygonHit = {
  id: string;
  bbox: RectBounds;
  points: Pos2[];
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DRAG_DELAY_MS = 120;
const CLICK_MOVE_TOLERANCE_PX = 4;

const SELECT_HIGHLIGHT: [number, number, number, number] = [100, 160, 255, 60];
const DIMENSION_COLOR: [number, number, number, number] = [25, 32, 39, 230];
const DIMENSION_TICK_LENGTH = 0.12;
const GEOMETRY_EPSILON = 1e-9;

const bboxToRectBounds = (bbox: BBox): RectBounds => ({
  minX: bbox.x,
  minY: bbox.y,
  maxX: bbox.x + bbox.width,
  maxY: bbox.y + bbox.height,
});

const rectsIntersect = (a: RectBounds, b: RectBounds): boolean =>
  a.minX <= b.maxX &&
  a.maxX >= b.minX &&
  a.minY <= b.maxY &&
  a.maxY >= b.minY;

const pointInRect = ([x, y]: Pos2, rect: RectBounds): boolean =>
  x >= rect.minX - GEOMETRY_EPSILON &&
  x <= rect.maxX + GEOMETRY_EPSILON &&
  y >= rect.minY - GEOMETRY_EPSILON &&
  y <= rect.maxY + GEOMETRY_EPSILON;

const pointInPolygon = ([px, py]: Pos2, polygon: Pos2[]): boolean => {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    const intersects =
      (yi > py) !== (yj > py) &&
      px < ((xj - xi) * (py - yi)) / ((yj - yi) || GEOMETRY_EPSILON) + xi;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const segmentOrientation = (a: Pos2, b: Pos2, c: Pos2): number =>
  (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

const pointOnSegment = (point: Pos2, start: Pos2, end: Pos2): boolean =>
  Math.abs(segmentOrientation(start, end, point)) <= GEOMETRY_EPSILON &&
  point[0] >= Math.min(start[0], end[0]) - GEOMETRY_EPSILON &&
  point[0] <= Math.max(start[0], end[0]) + GEOMETRY_EPSILON &&
  point[1] >= Math.min(start[1], end[1]) - GEOMETRY_EPSILON &&
  point[1] <= Math.max(start[1], end[1]) + GEOMETRY_EPSILON;

const segmentsIntersect = (a: Pos2, b: Pos2, c: Pos2, d: Pos2): boolean => {
  const o1 = segmentOrientation(a, b, c);
  const o2 = segmentOrientation(a, b, d);
  const o3 = segmentOrientation(c, d, a);
  const o4 = segmentOrientation(c, d, b);

  if (
    ((o1 > GEOMETRY_EPSILON && o2 < -GEOMETRY_EPSILON) ||
      (o1 < -GEOMETRY_EPSILON && o2 > GEOMETRY_EPSILON)) &&
    ((o3 > GEOMETRY_EPSILON && o4 < -GEOMETRY_EPSILON) ||
      (o3 < -GEOMETRY_EPSILON && o4 > GEOMETRY_EPSILON))
  ) {
    return true;
  }

  return (
    pointOnSegment(c, a, b) ||
    pointOnSegment(d, a, b) ||
    pointOnSegment(a, c, d) ||
    pointOnSegment(b, c, d)
  );
};

const polygonIntersectsRect = (
  polygon: Pos2[],
  polygonBbox: RectBounds,
  rect: RectBounds,
): boolean => {
  if (!rectsIntersect(polygonBbox, rect)) {
    return false;
  }

  if (polygon.some((point) => pointInRect(point, rect))) {
    return true;
  }

  const rectCorners: Pos2[] = [
    [rect.minX, rect.minY],
    [rect.maxX, rect.minY],
    [rect.maxX, rect.maxY],
    [rect.minX, rect.maxY],
  ];

  if (rectCorners.some((corner) => pointInPolygon(corner, polygon))) {
    return true;
  }

  const rectEdges: Array<[Pos2, Pos2]> = [
    [rectCorners[0], rectCorners[1]],
    [rectCorners[1], rectCorners[2]],
    [rectCorners[2], rectCorners[3]],
    [rectCorners[3], rectCorners[0]],
  ];

  for (let i = 0; i < polygon.length; i += 1) {
    const start = polygon[i];
    const end = polygon[(i + 1) % polygon.length];
    if (rectEdges.some(([a, b]) => segmentsIntersect(start, end, a, b))) {
      return true;
    }
  }

  return false;
};

const sameStringArray = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const formatDimensionDistance = (distanceM: number): string => {
  const millimeters = Math.round(distanceM * 1000);
  return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(
    millimeters,
  );
};

const getDimensionTickPath = (
  from: Pos2,
  to: Pos2,
  direction: -1 | 1,
): Pos2[] => {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  if (length <= Number.EPSILON) {
    return [from, from];
  }

  const normal: Pos2 = [
    (-dy / length) * DIMENSION_TICK_LENGTH,
    (dx / length) * DIMENSION_TICK_LENGTH,
  ];
  const center = direction === -1 ? from : to;

  return [
    [center[0] - normal[0] / 2, center[1] - normal[1] / 2],
    [center[0] + normal[0] / 2, center[1] + normal[1] / 2],
  ];
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DeckBimViewer: React.FC<DeckBimViewerProps> = (props) => {
  const {
    polygons,
    paths,
    circles = [],
    dimensionLines = [],
    angle = 180,
    onClick,
    onDragAndDrop,
    onDragSelected,
    onMoveStart,
    onRightMenuClick,
    pointerTracker,
    goToPolygon,
    action = "normal",
    zoomExtentKey,
    useLeftClickToPan = false,
    flip2dCoordinate = true,
    zoom: controlledZoom,
    centerWorld: controlledCenter,
    onCameraChange,
  } = props;

  // ---- Container size (for fit-zoom) ----
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setContainerSize({ w: Math.max(1, width), h: Math.max(1, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- Transform all geometry once ----
  const td = useMemo(
    () =>
      computeTransformedData(
        polygons,
        paths ?? [],
        circles,
        dimensionLines,
        angle,
        flip2dCoordinate,
      ),
    [polygons, paths, circles, dimensionLines, angle, flip2dCoordinate],
  );

  // ---- Camera (view state) ----
  const initialViewState = useMemo<ViewState>(() => {
    const zoom = computeFitZoom(td.bbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(td.bbox);
    return { target: [cx, cy, 0], zoom, minZoom: -5, maxZoom: 20 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // only on mount

  const [viewState, setViewState] = useState<ViewState>(initialViewState);

  const handleViewStateChange = useCallback(
    ({ viewState: vs }: { viewState: ViewState }) => {
      setViewState(vs);
      onCameraChange?.({
        zoom: vs.zoom,
        centerWorld: { x: vs.target[0], y: vs.target[1] },
      });
    },
    [onCameraChange],
  );

  // Controlled camera from props
  useEffect(() => {
    if (controlledZoom === undefined || controlledCenter === undefined) return;
    setViewState((prev) => ({
      ...prev,
      zoom: controlledZoom,
      target: [controlledCenter.x, controlledCenter.y, 0],
    }));
  }, [controlledZoom, controlledCenter]);

  // zoomExtentKey â†’ fit all to view
  const lastZoomKeyRef = useRef<string | number | null | undefined>(undefined);
  useEffect(() => {
    if (zoomExtentKey == null) return;
    if (lastZoomKeyRef.current === zoomExtentKey) return;
    lastZoomKeyRef.current = zoomExtentKey;
    const zoom = computeFitZoom(td.bbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(td.bbox);
    setViewState((prev) => ({ ...prev, zoom, target: [cx, cy, 0] }));
  }, [zoomExtentKey, td.bbox, containerSize]);

  // goToPolygon â†’ fly to specific polygon
  const lastGoToRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!goToPolygon || goToPolygon === lastGoToRef.current) return;
    lastGoToRef.current = goToPolygon;
    const p = td.polygons.find((p) => p.id === goToPolygon);
    if (!p) return;
    const pBbox = getBoundingBox(p.tPoints);
    const zoom = computeFitZoom(pBbox, containerSize.w, containerSize.h);
    const [cx, cy] = bboxCenter(pBbox);
    setViewState((prev) => ({ ...prev, zoom, target: [cx, cy, 0] }));
  }, [goToPolygon, td.polygons, containerSize]);

  // ---- Interaction state ----
  const deckRef = useRef<DeckGLRef>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  // Measure
  const [measurePts, setMeasurePts] = useState<Pos2[]>([]);
  const [measureMouse, setMeasureMouse] = useState<Pos2 | null>(null);

  // Drag-select
  const [selectState, setSelectState] = useState<SelectState | null>(null);
  const [preSelectedIds, setPreSelectedIds] = useState<string[]>([]);
  const selectStateRef = useRef<SelectState | null>(null);

  // Drag-and-drop
  const [dragOverride, setDragOverride] = useState<{
    id: string;
    tPoints: Pos2[];
  } | null>(null);
  const dragStateRef = useRef<DragState | null>(null);
  const dragTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isDraggingRef = useRef(false);

  // Pointer tracking for click vs drag discrimination
  const pointerDownPosRef = useRef<Pos2 | null>(null);
  const isPointerDownRef = useRef(false);
  const movedRef = useRef(false);

  // Last hovered polygon (for context menu on right-click)
  const lastHoveredPolygonRef = useRef<IPixPolygon | null>(null);

  // Whether a moveable polygon is hovered â€” disables dragPan so device drag works
  const [overMoveable, setOverMoveable] = useState(false);

  // Middle-mouse pan state
  const middlePanRef = useRef<{ screenX: number; screenY: number } | null>(
    null,
  );

  // Space key for pan
  const [spacePan, setSpacePan] = useState(false);
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpacePan(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpacePan(false);
    };
    const onBlur = () => setSpacePan(false);
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  // Clear measure points when leaving measure mode
  useEffect(() => {
    if (action !== "measure") {
      setMeasurePts([]);
      setMeasureMouse(null);
    }
  }, [action]);

  // ---- Viewport helper ----
  const getViewport = useCallback(() => {
    return (deckRef.current?.deck as any)?.getViewports?.()?.[0] ?? null;
  }, []);

  const unprojectScreen = useCallback(
    (screenX: number, screenY: number): Pos2 | null => {
      const vp = getViewport();
      if (!vp) return null;
      const w = vp.unproject([screenX, screenY]);
      return [w[0], w[1]];
    },
    [getViewport],
  );

  // ---- Drag-and-drop ----
  const startDrag = useCallback(
    (polygonId: string, screenX: number, screenY: number) => {
      const p = td.polygons.find((p) => p.id === polygonId);
      if (!p) return;
      const startWorld = unprojectScreen(screenX, screenY);
      if (!startWorld) return;
      onMoveStart?.(polygonId);
      isDraggingRef.current = true;
      dragStateRef.current = {
        polygonId,
        originalPoints: [...p.tPoints],
        startWorld,
      };
    },
    [td.polygons, unprojectScreen, onMoveStart],
  );

  const updateDrag = useCallback(
    (screenX: number, screenY: number) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const currentWorld = unprojectScreen(screenX, screenY);
      if (!currentWorld) return;
      const dx = currentWorld[0] - ds.startWorld[0];
      const dy = currentWorld[1] - ds.startWorld[1];
      const newPoints: Pos2[] = ds.originalPoints.map(([px, py]) => [
        px + dx,
        py + dy,
      ]);
      setDragOverride({ id: ds.polygonId, tPoints: newPoints });
    },
    [unprojectScreen],
  );

  const finishDrag = useCallback(() => {
    const ds = dragStateRef.current;
    if (!ds) return;
    isDraggingRef.current = false;
    dragStateRef.current = null;

    const finalTPoints =
      dragOverride?.id === ds.polygonId
        ? dragOverride.tPoints
        : ds.originalPoints;

    setDragOverride(null);

    const modelPoints = finalTPoints.map((tp) =>
      invertTransform(tp, td.center, td.angleDeg, flip2dCoordinate),
    );
    onDragAndDrop?.(ds.polygonId, modelPoints);
  }, [dragOverride, td.center, td.angleDeg, flip2dCoordinate, onDragAndDrop]);

  // ---- Build layer data (apply drag override) ----
  const polygonLayerData = useMemo(() => {
    if (!dragOverride) return td.polygons;
    return td.polygons.map((p) =>
      p.id === dragOverride.id ? { ...p, tPoints: dragOverride.tPoints } : p,
    );
  }, [td.polygons, dragOverride]);

  const selectablePolygonHits = useMemo<SelectablePolygonHit[]>(
    () =>
      polygonLayerData
        .filter((polygon) => polygon.isDragSelectable)
        .map((polygon) => ({
          id: polygon.id,
          points: polygon.tPoints,
          bbox: bboxToRectBounds(getBoundingBox(polygon.tPoints)),
        })),
    [polygonLayerData],
  );

  const setLivePreSelectedIds = useCallback((ids: string[]) => {
    setPreSelectedIds((prev) => (sameStringArray(prev, ids) ? prev : ids));
  }, []);

  // ---- Layers ----
  const layers = useMemo(() => {
    // console.time('[PixBimV2] layers useMemo');
    // Classify polygons: hollow = outline-only (white/no fill), filled = has a real fill color
    type PolyEdge = {
      from: Pos2;
      to: Pos2;
      strokeColor: string | undefined;
      strokeWidth: string | undefined;
    };
    const isHollow = (p: TransformedPolygon) => {
      const hex = p.fillColor?.replace("#", "").toLowerCase();
      return !hex || hex === "fff" || hex === "ffffff";
    };
    const polygonGroups = Array.from(
      polygonLayerData.reduce((groups, polygon) => {
        const zIndex = polygon.zIndex ?? 0;
        const group = groups.get(zIndex) ?? [];
        group.push(polygon);
        groups.set(zIndex, group);
        return groups;
      }, new Map<number, TransformedPolygon[]>()),
    ).sort(([a], [b]) => a - b);

    const toEdges = (polys: TransformedPolygon[]): PolyEdge[] =>
      polys.flatMap((poly) =>
        poly.tPoints.map((pt, i) => ({
          from: pt,
          to: poly.tPoints[(i + 1) % poly.tPoints.length],
          strokeColor: poly.strokeColor,
          strokeWidth: poly.strokeWidth,
        })),
      );

    const getPolygonTextSize = (d: TransformedPolygon) => {
      // Base size from polygon bounding box (mirrors old PixBimPolygon sizeConstant logic).
      // text.fontSize is a multiplier: "1" = default, "2" = double, etc.
      const xs = d.tPoints.map((p) => p[0]);
      const ys = d.tPoints.map((p) => p[1]);
      const w = Math.max(...xs) - Math.min(...xs);
      const h = Math.max(...ys) - Math.min(...ys);
      const shortSide = Math.min(w, h);
      const base = Math.min(shortSide, 2.5) / 6;
      const scale = parseFontSize(d.text.fontSize, 1);
      return base * scale;
    };

    const polygonLayers = polygonGroups.flatMap(([zIndex, group]) => {
      const hollowPolys = group.filter(isHollow);
      const filledPolys = group.filter((p) => !isHollow(p));
      const textPolys = group.filter(
        (d: TransformedPolygon) => d.text?.show && d.text.values?.length,
      );

      const hollowStrokeLayer = new LineLayer<PolyEdge>({
        id: `polygon-hollow-strokes-${zIndex}`,
        data: toEdges(hollowPolys),
        pickable: false,
        getSourcePosition: (d: PolyEdge) =>
          [...d.from, 0] as [number, number, number],
        getTargetPosition: (d: PolyEdge) =>
          [...d.to, 0] as [number, number, number],
        getColor: (d: PolyEdge) => hexToRgba(d.strokeColor, 1),
        getWidth: (d: PolyEdge) => parseStrokeWidth(d.strokeWidth, 1) / 100,
        widthUnits: "common",
        widthMinPixels: 0.5,
      });

      const hollowHitLayer = new SolidPolygonLayer<TransformedPolygon>({
        id: `polygon-hollow-hit-${zIndex}`,
        data: hollowPolys,
        pickable: true,
        filled: true,
        getPolygon: (d: TransformedPolygon) => d.tPoints,
        getFillColor: [0, 0, 0, 0],
        _normalize: true,
      });

      const polygonFillLayer = new SolidPolygonLayer<TransformedPolygon>({
        id: `polygons-${zIndex}`,
        data: filledPolys,
        pickable: true,
        filled: true,
        getPolygon: (d: TransformedPolygon) => d.tPoints,
        getFillColor: (d: TransformedPolygon) =>
          hexToRgba(d.fillColor, d.opacity ?? 1),
        _normalize: true,
      });

      const polygonStrokeLayer = new LineLayer<PolyEdge>({
        id: `polygon-strokes-${zIndex}`,
        data: toEdges(filledPolys),
        pickable: false,
        getSourcePosition: (d: PolyEdge) =>
          [...d.from, 0] as [number, number, number],
        getTargetPosition: (d: PolyEdge) =>
          [...d.to, 0] as [number, number, number],
        getColor: (d: PolyEdge) => hexToRgba(d.strokeColor, 1),
        getWidth: (d: PolyEdge) => parseStrokeWidth(d.strokeWidth, 1) / 100,
        widthUnits: "common",
        widthMinPixels: 0.5,
      });

      const polygonTextLayer = new TextLayer<TransformedPolygon>({
        id: `polygon-labels-${zIndex}`,
        data: textPolys,
        getPosition: (d: TransformedPolygon) =>
          [...polygonCentroid(d.tPoints), 0] as [number, number, number],
        getText: (d: TransformedPolygon) => (d.text.values ?? []).join("\n"),
        getColor: (d: TransformedPolygon) => hexToRgba(d.text.color, 1),
        getSize: getPolygonTextSize,
        sizeUnits: "common",
        getTextAnchor: "middle",
        getAlignmentBaseline: "center",
        characterSet: "auto",
        pickable: false,
      });

      return [
        hollowStrokeLayer,
        hollowHitLayer,
        polygonFillLayer,
        polygonStrokeLayer,
        polygonTextLayer,
      ];
    });

    // --- Paths ---
    type TPath = (typeof td.paths)[0];
    type TCircle = (typeof td.circles)[0];
    type TDimensionLine = (typeof td.dimensionLines)[0];
    type MeasurePt = { path: Pos2[] };
    type DimensionPath = { id: string; path: Pos2[] };

    const pathLayer = new PathLayer<TPath>({
      id: "paths",
      data: td.paths,
      pickable: true,
      getPath: (d: TPath) => d.tPoints,
      getColor: (d: TPath) => hexToRgba(d.strokeColor, 1),
      getWidth: (d: TPath) => parseStrokeWidth(d.strokeWidth, 1) / 100,
      widthUnits: "common",
      jointRounded: true,
      capRounded: true,
    });

    // --- Circles ---
    const circleLayer = new ScatterplotLayer<TCircle>({
      id: "circles",
      data: td.circles,
      pickable: true,
      getPosition: (d: TCircle) => [...d.tPos, 0] as [number, number, number],
      getRadius: (d: TCircle) => d.radius,
      getFillColor: (d: TCircle) => hexToRgba(d.color, d.opacity ?? 1),
      getLineColor: [80, 80, 80, 255],
      stroked: false,
    });

    // --- Text on circles ---
    const circleTextData = td.circles.filter(
      (d: TCircle) => d.text?.show && d.text.values?.length,
    );
    const circleTextLayer = new TextLayer<TCircle>({
      id: "circle-labels",
      data: circleTextData,
      getPosition: (d: TCircle) => [...d.tPos, 0] as [number, number, number],
      getText: (d: TCircle) => (d.text!.values ?? []).join("\n"),
      getColor: (d: TCircle) => hexToRgba(d.text!.color, 1),
      getSize: (d: TCircle) => parseFontSize(d.text!.fontSize, 12) / 100,
      sizeUnits: "common",
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      characterSet: "auto",
      pickable: false,
    });

    // --- Measure line ---
    const measureLinePts: MeasurePt[] = [];
    if (measurePts.length >= 1) {
      const end =
        measurePts.length === 2
          ? measurePts[1]
          : (measureMouse ?? measurePts[0]);
      measureLinePts.push({ path: [measurePts[0], end] });
    }
    const measureLayer = new PathLayer<MeasurePt>({
      id: "measure",
      data: measureLinePts,
      getPath: (d: MeasurePt) => d.path,
      getColor: [220, 80, 80, 220],
      getWidth: 2,
      widthUnits: "pixels",
      pickable: false,
    });

    // --- Dimension lines ---
    const dimensionPathData: DimensionPath[] = td.dimensionLines.flatMap(
      (dimensionLine) => {
        const [from, to] = dimensionLine.tPoints;

        return [
          {
            id: `${dimensionLine.id}-line`,
            path: [from, to],
          },
          {
            id: `${dimensionLine.id}-from-tick`,
            path: getDimensionTickPath(from, to, -1),
          },
          {
            id: `${dimensionLine.id}-to-tick`,
            path: getDimensionTickPath(from, to, 1),
          },
        ];
      },
    );
    const dimensionLineLayer = new PathLayer<DimensionPath>({
      id: "dimension-lines",
      data: dimensionPathData,
      getPath: (d: DimensionPath) => d.path,
      getColor: DIMENSION_COLOR,
      getWidth: 1.2,
      widthUnits: "pixels",
      pickable: false,
    });

    const dimensionLabelLayer = new TextLayer<TDimensionLine>({
      id: "dimension-line-labels",
      data: td.dimensionLines,
      getPosition: (d: TDimensionLine) => {
        const [from, to] = d.tPoints;
        const dx = to[0] - from[0];
        const dy = to[1] - from[1];
        const length = Math.hypot(dx, dy);
        const offset =
          length > Number.EPSILON ? DIMENSION_TICK_LENGTH * 0.65 : 0;
        const normal: Pos2 =
          length > Number.EPSILON ? [-dy / length, dx / length] : [0, -1];

        return [
          (from[0] + to[0]) / 2 + normal[0] * offset,
          (from[1] + to[1]) / 2 + normal[1] * offset,
          0,
        ] as [number, number, number];
      },
      getText: (d: TDimensionLine) => formatDimensionDistance(d.distance),
      getColor: DIMENSION_COLOR,
      getSize: 14,
      sizeUnits: "pixels",
      getTextAnchor: "middle",
      getAlignmentBaseline: "center",
      characterSet: "auto",
      pickable: false,
    });

    // --- Drag-select highlight overlay ---
    const preSelectedIdSet = new Set(preSelectedIds);
    const preSelected = td.polygons.filter((p) =>
      preSelectedIdSet.has(p.id),
    );
    const selectHighlightLayer = new PolygonLayer<TransformedPolygon>({
      id: "select-highlight",
      data: preSelected,
      getPolygon: (d: TransformedPolygon) => d.tPoints,
      getFillColor: SELECT_HIGHLIGHT,
      stroked: false,
      filled: true,
      pickable: false,
      _normalize: true,
    });

    const result = [
      ...polygonLayers,
      pathLayer,
      circleLayer,
      circleTextLayer,
      measureLayer,
      dimensionLineLayer,
      dimensionLabelLayer,
      selectHighlightLayer,
    ];
    return result;
  }, [
    polygonLayerData,
    td.paths,
    td.circles,
    td.dimensionLines,
    td.polygons,
    td.center,
    td.angleDeg,
    measurePts,
    measureMouse,
    preSelectedIds,
  ]);

  // ---- deck.gl pick helpers ----
  const pickAtScreen = useCallback((x: number, y: number) => {
    return (
      (deckRef.current?.deck as any)?.pickObject({ x, y, radius: 4 }) ?? null
    );
  }, []);

  const getWorldRectFromScreenRect = useCallback(
    (startScreen: Pos2, currentScreen: Pos2): RectBounds | null => {
      const minX = Math.min(startScreen[0], currentScreen[0]);
      const minY = Math.min(startScreen[1], currentScreen[1]);
      const maxX = Math.max(startScreen[0], currentScreen[0]);
      const maxY = Math.max(startScreen[1], currentScreen[1]);

      const corners = [
        unprojectScreen(minX, minY),
        unprojectScreen(maxX, minY),
        unprojectScreen(maxX, maxY),
        unprojectScreen(minX, maxY),
      ].filter((point): point is Pos2 => point != null);

      if (corners.length === 0) {
        return null;
      }

      return {
        minX: Math.min(...corners.map((point) => point[0])),
        minY: Math.min(...corners.map((point) => point[1])),
        maxX: Math.max(...corners.map((point) => point[0])),
        maxY: Math.max(...corners.map((point) => point[1])),
      };
    },
    [unprojectScreen],
  );

  const selectPolygonsInScreenRect = useCallback(
    (startScreen: Pos2, currentScreen: Pos2): string[] => {
      const rect = getWorldRectFromScreenRect(startScreen, currentScreen);
      if (!rect) {
        return [];
      }

      return selectablePolygonHits
        .filter((polygon) =>
          polygonIntersectsRect(polygon.points, polygon.bbox, rect),
        )
        .map((polygon) => polygon.id);
    },
    [getWorldRectFromScreenRect, selectablePolygonHits],
  );

  // ---- Hover ----
  const handleHover = useCallback((info: PickingInfo) => {
    if (!info.object) {
      setTooltip(null);
      setOverMoveable(false);
      lastHoveredPolygonRef.current = null;
      return;
    }
    const obj = info.object as TransformedPolygon | IPixPath;

    const layerId = info.layer?.id;
    const poly =
      layerId?.startsWith("polygons-") ||
      layerId?.startsWith("polygon-hollow-hit-")
        ? (obj as TransformedPolygon)
        : null;
    if (poly) {
      lastHoveredPolygonRef.current = poly;
      setOverMoveable(poly.isMoveable ?? false);
      if (poly.tooltip) {
        setTooltip({ texts: [poly.tooltip], x: info.x + 16, y: info.y + 16 });
      } else {
        setTooltip(null);
      }
    } else {
      lastHoveredPolygonRef.current = null;
      const path = info.layer?.id === "paths" ? (obj as IPixPath) : null;
      if (path?.tooltip) {
        setTooltip({ texts: [path.tooltip], x: info.x + 16, y: info.y + 16 });
      } else {
        setTooltip(null);
      }
    }
  }, []);

  // ---- Click (deck.gl global) ----
  // Fires for both empty-canvas clicks and object clicks; we route accordingly.
  const handleDeckClick = useCallback(
    (info: PickingInfo) => {
      if (action === "measure" || action === "dragSelect") return;
      if (movedRef.current) return;
      if (spacePan) return;
      if (!info.object) {
        onClick?.(undefined, [info.x, info.y]);
        return;
      }
      const obj = info.object as TransformedPolygon | IPixCircle;
      if ((obj as TransformedPolygon).isNotClickable) return;
      onClick?.(obj.id, [info.x, info.y]);
    },
    [action, onClick, spacePan],
  );

  // ---- Controller options ----
  // Pan rules (mirrors old PixBimViewport behaviour):
  //   useLeftClickToPan (dev)  â†’ left mouse always pans (deck.gl dragPan)
  //   spacePan                 â†’ left mouse pans while Space held (deck.gl dragPan)
  //   otherwise                â†’ middle mouse pans (manual, deck.gl dragPan off)
  // dragPan is also disabled during dragSelect / measure so those modes work correctly.
  const leftDragPanActive =
    (useLeftClickToPan || spacePan) &&
    action !== "dragSelect" &&
    action !== "measure" &&
    !overMoveable;
  const controllerOptions = useMemo(
    () => ({
      dragPan: leftDragPanActive,
      scrollZoom: true,
      doubleClickZoom: false,
      keyboard: false,
    }),
    [leftDragPanActive],
  );

  // ---- Container pointer events ----
  const getScreenPos = (e: React.PointerEvent | PointerEvent): Pos2 => {
    const rect = containerRef.current!.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      const [sx, sy] = getScreenPos(e);

      // Middle mouse button â€” start pan
      if (e.button === 1) {
        e.preventDefault();
        middlePanRef.current = { screenX: sx, screenY: sy };
        return;
      }

      pointerDownPosRef.current = [sx, sy];
      isPointerDownRef.current = true;
      movedRef.current = false;
      setContextMenu(null);

      if (action === "measure") {
        const world = unprojectScreen(sx, sy);
        if (!world) return;
        setMeasurePts((prev) => {
          if (prev.length === 0) return [world];
          return [prev[0], world]; // finalize measurement with second click
        });
        return;
      }

      if (action === "dragSelect") {
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          // Some test/browser environments do not support pointer capture.
        }
        selectStateRef.current = {
          startScreen: [sx, sy],
          currentScreen: [sx, sy],
        };
        setSelectState({ startScreen: [sx, sy], currentScreen: [sx, sy] });
        setLivePreSelectedIds([]);
        return;
      }

      // Normal mode: check for drag-and-drop
      const picked = pickAtScreen(sx, sy);
      const poly = picked?.object as TransformedPolygon | null;
      if (poly?.isMoveable) {
        dragTimerRef.current = setTimeout(() => {
          startDrag(poly.id, sx, sy);
        }, DRAG_DELAY_MS);
      }
    },
    [action, unprojectScreen, pickAtScreen, startDrag, setLivePreSelectedIds],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const [sx, sy] = getScreenPos(e);

      // Middle-mouse pan
      if (middlePanRef.current) {
        const dx = sx - middlePanRef.current.screenX;
        const dy = sy - middlePanRef.current.screenY;
        middlePanRef.current = { screenX: sx, screenY: sy };
        const scale = 1 / Math.pow(2, viewState.zoom);
        setViewState((prev) => ({
          ...prev,
          target: [
            prev.target[0] - dx * scale,
            prev.target[1] - dy * scale,
            0,
          ] as [number, number, number],
        }));
        return;
      }

      // Measure â€“ live line endpoint
      if (action === "measure" && measurePts.length === 1) {
        const world = unprojectScreen(sx, sy);
        if (world) setMeasureMouse(world);
      }

      // Drag-select â€“ update rect
      if (action === "dragSelect" && selectStateRef.current) {
        const next = {
          ...selectStateRef.current,
          currentScreen: [sx, sy] as Pos2,
        };
        selectStateRef.current = next;
        setSelectState(next);

        const { startScreen, currentScreen } = next;
        setLivePreSelectedIds(
          selectPolygonsInScreenRect(startScreen, currentScreen),
        );
        return;
      }

      // Check movement for click vs drag discrimination
      if (isPointerDownRef.current && pointerDownPosRef.current) {
        const dx = sx - pointerDownPosRef.current[0];
        const dy = sy - pointerDownPosRef.current[1];
        if (
          dx * dx + dy * dy >
          CLICK_MOVE_TOLERANCE_PX * CLICK_MOVE_TOLERANCE_PX
        ) {
          movedRef.current = true;
          // Don't cancel the drag timer on small movement â€” natural hand jitter would
          // otherwise prevent device dragging from ever starting.
        }
      }

      // Drag-and-drop move
      if (isDraggingRef.current) {
        updateDrag(sx, sy);
      }
    },
    [
      action,
      measurePts,
      unprojectScreen,
      selectPolygonsInScreenRect,
      setLivePreSelectedIds,
      updateDrag,
    ],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Ignore environments without pointer capture.
      }

      if (e.button === 1) {
        middlePanRef.current = null;
        return;
      }
      isPointerDownRef.current = false;

      if (dragTimerRef.current) {
        clearTimeout(dragTimerRef.current);
        dragTimerRef.current = null;
      }

      if (isDraggingRef.current) {
        finishDrag();
        return;
      }

      if (action === "dragSelect") {
        const ss = selectStateRef.current;
        if (ss) {
          const { startScreen, currentScreen } = ss;
          const ids = selectPolygonsInScreenRect(startScreen, currentScreen);
          onDragSelected?.(ids);
        }
        selectStateRef.current = null;
        setSelectState(null);
        setLivePreSelectedIds([]);
        return;
      }
    },
    [
      action,
      finishDrag,
      selectPolygonsInScreenRect,
      onDragSelected,
      setLivePreSelectedIds,
    ],
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent) => {
      try {
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      } catch {
        // Ignore environments without pointer capture.
      }

      isPointerDownRef.current = false;
      middlePanRef.current = null;
      selectStateRef.current = null;
      setSelectState(null);
      setLivePreSelectedIds([]);
    },
    [setLivePreSelectedIds],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const poly = lastHoveredPolygonRef.current;
    if (poly?.rightClickMenu?.length) {
      const rect = containerRef.current!.getBoundingClientRect();
      setContextMenu({
        polygon: poly,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, []);

  // ---- Measure distance label ----
  const measureDistance = useMemo(() => {
    if (measurePts.length < 2) return null;
    const [a, b] = measurePts;
    // Distance is in model space units (meters typically)
    const dist = Math.sqrt((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2);
    const midX = (a[0] + b[0]) / 2;
    const midY = (a[1] + b[1]) / 2;
    return { dist, midWorld: [midX, midY] as Pos2 };
  }, [measurePts]);

  const measureLabelScreen = useMemo(() => {
    if (!measureDistance) return null;
    const vp = getViewport();
    if (!vp) return null;
    const [sx, sy] = vp.project([...measureDistance.midWorld, 0]);
    return { x: sx, y: sy, dist: measureDistance.dist };
  }, [measureDistance, viewState, getViewport]); // viewState dep ensures recalc on pan/zoom

  // ---- Pointer tracker ----
  const [pointerPos, setPointerPos] = useState<Pos2 | null>(null);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!pointerTracker) return;
      const rect = containerRef.current!.getBoundingClientRect();
      setPointerPos([e.clientX - rect.left, e.clientY - rect.top]);
    },
    [pointerTracker],
  );
  const handleMouseLeave = useCallback(() => setPointerPos(null), []);

  // ---- Selection rect DOM style ----
  const selectRectStyle: React.CSSProperties | null = useMemo(() => {
    if (!selectState) return null;
    const { startScreen, currentScreen } = selectState;
    return {
      position: "absolute",
      left: Math.min(startScreen[0], currentScreen[0]),
      top: Math.min(startScreen[1], currentScreen[1]),
      width: Math.abs(currentScreen[0] - startScreen[0]),
      height: Math.abs(currentScreen[1] - startScreen[1]),
      border: "1px dashed #3b82f6",
      background: "rgba(59,130,246,0.08)",
      pointerEvents: "none",
    };
  }, [selectState]);

  // ---- Render ----
  const view = useMemo(
    () => new OrthographicView({ id: "ortho", flipY: true }),
    [],
  );

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
      onContextMenu={handleContextMenu}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onContextMenuCapture={(e) => e.preventDefault()}
    >
      <DeckGL
        ref={deckRef}
        views={view}
        viewState={viewState}
        onViewStateChange={handleViewStateChange as any}
        controller={controllerOptions}
        layers={layers}
        onHover={handleHover}
        onClick={handleDeckClick as any}
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          background: "#ffffff",
        }}
        parameters={{ depthTest: false, depthMask: false } as any}
      />

      {/* Selection rect overlay */}
      {selectRectStyle && <div style={selectRectStyle} />}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y,
            background: "#ffffff",
            border: "1px solid #999",
            borderRadius: 3,
            padding: "4px 8px",
            fontSize: 12,
            pointerEvents: "none",
            whiteSpace: "pre-line",
            zIndex: 10,
            maxWidth: 260,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          }}
        >
          {tooltip.texts.join("\n")}
        </div>
      )}

      {/* Measure distance label */}
      {measureLabelScreen && (
        <div
          style={{
            position: "absolute",
            left: measureLabelScreen.x,
            top: measureLabelScreen.y - 22,
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.75)",
            color: "#fff",
            borderRadius: 3,
            padding: "2px 7px",
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {measureLabelScreen.dist.toFixed(2)} m
        </div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{
            position: "absolute",
            left: contextMenu.x,
            top: contextMenu.y,
            background: "#EEEEEE",
            border: "1px solid #999",
            borderRadius: 4,
            minWidth: 150,
            zIndex: 20,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          }}
        >
          {contextMenu.polygon.rightClickMenu?.map((label) => (
            <div
              key={label}
              style={{
                padding: "6px 12px",
                cursor: "pointer",
                fontSize: 14,
                userSelect: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "#ddd";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = "";
              }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                onRightMenuClick?.(label, contextMenu.polygon);
                setContextMenu(null);
              }}
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {/* Pointer tracker circle */}
      {pointerTracker && pointerPos && (
        <div
          style={{
            position: "absolute",
            left: pointerPos[0] - pointerTracker.size / 2,
            top: pointerPos[1] - pointerTracker.size / 2,
            width: pointerTracker.size,
            height: pointerTracker.size,
            borderRadius: "50%",
            background: pointerTracker.color,
            pointerEvents: "none",
            opacity: 0.6,
            zIndex: 5,
          }}
        />
      )}
    </div>
  );
};

export default DeckBimViewer;
